import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Search, Plus, Copy, Check, Edit, Eye, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Table, Column } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { Toast } from '../../components/ui/Toast';
import { Pagination } from '../../components/ui/Pagination';
import { ApplicationForm } from '../../components/forms/ApplicationForm';
import { api } from '../../services/api';
import { isPublicSolicitudContestada } from '../../utils/solicitudNotes';
import { Application, Property, Unit, Prospect } from '../../types';
import { formatDate, todayLocalDateString } from '../../utils/date';
import { buildCsv, downloadCsvFile } from '../../utils/exportCsv';
import { fetchAllPagesData } from '../../utils/fetchAllPages';

type ApplicationFormPayload = Omit<Application, 'id' | 'prospectId' | 'prospectName' | 'propertyName'> & {
  firstName: string;
  lastName: string;
  monthlyRent: number;
  tipoUso: 'Habitacional' | 'Comercial';
  selectedProspectId?: string;
  email?: string;
  phone?: string;
};

function mapApiApplicationToApp(item: any): Application {
  const submittedDate = item.submittedDate;
  const dateStr = typeof submittedDate === 'string' ? submittedDate : submittedDate ? new Date(submittedDate).toISOString().split('T')[0] : '';
  return {
    id: item.id,
    prospectId: item.prospectId,
    prospectName: item.prospects?.name,
    prospectEmail: item.prospects?.email ?? '',
    prospectPhone: item.prospects?.phone ?? '',
    propertyId: item.propertyId,
    propertyName: item.properties?.name,
    unitNumber: item.unitNumber,
    submittedDate: dateStr,
    status: item.status,
    notes: item.notes,
    contractMonths: item.contractMonths,
    solicitudContestada: item.solicitudContestada === true,
  };
}

function filterApplicationRows(
  apps: Application[],
  searchTerm: string,
  statusFilter: 'all' | 'pending' | 'approved' | 'rejected',
): Application[] {
  return apps.filter((app) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch =
      app.prospectName?.toLowerCase().includes(q) ||
      app.propertyName?.toLowerCase().includes(q) ||
      (app.prospectEmail && app.prospectEmail.toLowerCase().includes(q)) ||
      (app.prospectPhone && String(app.prospectPhone).toLowerCase().includes(q));
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
}

function applicationStatusCsv(status: string): string {
  if (status === 'approved') return 'Aprobada';
  if (status === 'pending') return 'Pendiente';
  if (status === 'rejected') return 'Rechazada';
  return status;
}

const limit = 20;

export const Applications: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingApplication, setEditingApplication] = useState<Application | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [exportingCsv, setExportingCsv] = useState(false);

  const { data: pageData, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['applications-page', page],
    queryFn: async () => {
      const [applicationsRes, propertiesData, unitsRes, prospectsRes] = await Promise.all([
        api.getApplications({ page, limit }),
        api.getProperties({ limit: 500 }).then((r) => r?.data ?? []),
        api.getUnits(undefined, { limit: 500 }),
        api.getProspects({ limit: 500 }),
      ]);
      const applicationsList = applicationsRes?.data ?? [];
      const unitsList = Array.isArray(unitsRes) ? unitsRes : (unitsRes as any)?.data ?? [];
      const prospectsList = prospectsRes?.data ?? [];
      return {
        applications: Array.isArray(applicationsList) ? applicationsList.map(mapApiApplicationToApp) : [],
        total: applicationsRes?.total ?? 0,
        totalPages: applicationsRes?.totalPages ?? 0,
        properties: (propertiesData || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          address: p.address,
          city: p.city,
          state: p.state || '',
          unitCount: p.unitCount ?? 0,
          occupiedUnits: p.occupiedUnits ?? 0,
          m2Total: p.m2Total ?? 0,
          pricePerM2: p.pricePerM2 ?? 0,
          totalValue: p.totalValue ?? 0,
          status: p.status || 'en-proyecto',
          coverImage: p.coverImage,
          createdAt: p.createdAt,
        })),
        units: (unitsList || []).map((u: any) => ({
          id: u.id,
          propertyId: u.propertyId,
          unitNumber: u.unitNumber,
          m2: u.m2,
          bedrooms: u.bedrooms,
          bathrooms: u.bathrooms,
          status: u.status || 'available',
          tenantId: u.tenantId,
          monthlyRent: u.monthlyRent,
          contractStartDate: u.contractStartDate,
          contractEndDate: u.contractEndDate,
          notes: u.notes,
        })),
        prospects: (prospectsList || []).map((p: any) => ({
          id: p.id,
          name: p.name,
          email: p.email,
          phone: p.phone,
          propertyId: p.propertyId,
          propertyName: p.properties?.name,
          contactDate: p.contactDate ? (typeof p.contactDate === 'string' ? p.contactDate : new Date(p.contactDate).toISOString().split('T')[0]) : '',
          status: p.status || 'new',
          approvalStatus: p.approvalStatus,
          notes: p.notes,
        })),
      };
    },
    staleTime: 60_000,
  });

  const applications = pageData?.applications ?? [];
  const total = pageData?.total ?? 0;
  const totalPages = pageData?.totalPages ?? 0;
  const properties = pageData?.properties ?? [];
  const units = pageData?.units ?? [];
  const prospects = pageData?.prospects ?? [];
  const error = queryError ? (queryError as Error)?.message ?? 'Error al cargar los datos' : null;

  const createApplicationMutation = useMutation({
    mutationFn: async (applicationData: ApplicationFormPayload) => {
      let prospectId = applicationData.selectedProspectId;
      if (!prospectId) {
        const newProspect = await api.createProspect({
          name: `${applicationData.firstName} ${applicationData.lastName}`.trim(),
          email: applicationData.email!,
          phone: applicationData.phone!,
          propertyId: applicationData.propertyId,
          contactDate: applicationData.submittedDate,
          status: 'new',
        });
        prospectId = newProspect.id;
      }
      await api.createApplication({
        prospectId,
        propertyId: applicationData.propertyId,
        unitNumber: applicationData.unitNumber,
        submittedDate: applicationData.submittedDate,
        status: applicationData.status || 'pending',
        notes: applicationData.notes,
        monthlyRent: applicationData.monthlyRent,
        tipoUso: applicationData.tipoUso,
        contractMonths: applicationData.contractMonths,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications-page'] });
      setPage(1);
      setIsModalOpen(false);
      setToast({ message: 'Solicitud creada correctamente', type: 'success' });
    },
    onError: (err: Error) => {
      setToast({ message: err.message || 'Error al crear la solicitud', type: 'error' });
    },
  });

  const updateApplicationMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ApplicationFormPayload }) =>
      api.updateApplication(id, {
        prospectId: data.selectedProspectId ?? undefined,
        propertyId: data.propertyId,
        unitNumber: data.unitNumber,
        submittedDate: data.submittedDate,
        status: data.status,
        notes: data.notes,
        monthlyRent: data.monthlyRent,
        tipoUso: data.tipoUso,
        contractMonths: data.contractMonths,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications-page'] });
      setIsEditModalOpen(false);
      setEditingApplication(undefined);
      setToast({ message: 'Solicitud actualizada correctamente', type: 'success' });
    },
    onError: (err: Error) => {
      setToast({ message: err.message || 'Error al actualizar la solicitud', type: 'error' });
    },
  });

  const filteredApplications = useMemo(
    () => filterApplicationRows(applications, searchTerm, statusFilter),
    [searchTerm, statusFilter, applications],
  );

  const handleExportCsv = useCallback(async () => {
    setExportingCsv(true);
    try {
      const raw = await fetchAllPagesData((args) => api.getApplications(args));
      const all = raw.map(mapApiApplicationToApp);
      const rows = filterApplicationRows(all, searchTerm, statusFilter);
      const headers = [
        'ID',
        'Prospecto',
        'Correo',
        'Teléfono',
        'Propiedad',
        'Unidad',
        'Fecha solicitud',
        'Estado',
        'Solicitud contestada',
      ];
      const csvRows = rows.map((row) => [
        row.id,
        row.prospectName ?? '',
        row.prospectEmail ?? '',
        row.prospectPhone != null ? String(row.prospectPhone) : '',
        row.propertyName ?? '',
        row.unitNumber ?? '',
        formatDate(row.submittedDate),
        applicationStatusCsv(row.status),
        row.solicitudContestada === true || isPublicSolicitudContestada(row.notes) ? 'Sí' : 'No',
      ]);
      downloadCsvFile(`solicitudes-${todayLocalDateString()}.csv`, buildCsv(headers, csvRows));
    } catch (e) {
      alert((e as Error)?.message || 'Error al exportar CSV');
    } finally {
      setExportingCsv(false);
    }
  }, [searchTerm, statusFilter]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenEditModal = (application: Application) => {
    setEditingApplication(application);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingApplication(undefined);
  };

  const handleSubmit = (applicationData: ApplicationFormPayload) => {
    if (!applicationData.selectedProspectId && (!applicationData.email || !applicationData.phone)) {
      setToast({ message: 'Email y teléfono son requeridos para un prospecto nuevo', type: 'error' });
      return;
    }
    createApplicationMutation.mutate(applicationData);
  };

  const handleEditSubmit = (applicationData: ApplicationFormPayload) => {
    if (!editingApplication) return;
    updateApplicationMutation.mutate({ id: editingApplication.id, data: applicationData });
  };

  const handleCopyLink = async (applicationId: string) => {
    try {
      const res = await api.getSolicitudPublicLink(applicationId);
      const link = res?.solicitudUrl;
      if (!link || typeof link !== 'string') throw new Error('No se pudo obtener el link público');
      await navigator.clipboard.writeText(link);
      setCopiedId(applicationId);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (err) {
      console.error('Error al copiar el link:', err);
    }
  };

  const columns: Column<Application>[] = [
    { header: 'Prospecto', accessor: 'prospectName' },
    { header: 'Propiedad', accessor: 'propertyName' },
    { header: 'Unidad', accessor: 'unitNumber' },
    {
      header: 'Fecha Solicitud',
      accessor: (row) => formatDate(row.submittedDate),
      sortable: true,
    },
    {
      header: 'Estado',
      accessor: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'approved'
              ? 'bg-green-100 text-green-800'
              : row.status === 'pending'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.status === 'approved'
            ? 'Aprobada'
            : row.status === 'pending'
            ? 'Pendiente'
            : 'Rechazada'}
        </span>
      ),
    },
    {
      header: 'Acciones',
      accessor: (row) => (
        <div className="flex items-center space-x-2">
          {/*
            La info del formulario público es JSON en `application.notes` con `savedAt`.
            El texto que guarda el admin al crear la solicitud no cuenta como contestada.
          */}
          {(() => {
            const hasSolicitudContestada =
              row.solicitudContestada === true || isPublicSolicitudContestada(row.notes);
            return (
              <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleCopyLink(row.id);
            }}
            className={`inline-flex items-center justify-center p-2 rounded-lg text-sm font-medium transition-colors ${
              copiedId === row.id
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            title="Copiar link de la solicitud"
            aria-label={copiedId === row.id ? 'Link copiado' : 'Copiar link de la solicitud'}
          >
            {copiedId === row.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditModal(row);
            }}
            className="inline-flex items-center justify-center p-2 rounded-lg text-sm font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            title="Ver/Editar solicitud"
            aria-label="Ver o editar solicitud"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!hasSolicitudContestada) return;
              navigate(`/rentas/solicitudes/${row.id}`);
            }}
            disabled={!hasSolicitudContestada}
            className={`inline-flex items-center justify-center p-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors ${
              !hasSolicitudContestada ? 'opacity-50 cursor-not-allowed hover:bg-gray-100' : ''
            }`}
            title={
              hasSolicitudContestada
                ? 'Ver información capturada'
                : 'El prospecto aún no ha contestado la solicitud'
            }
            aria-label={
              hasSolicitudContestada
                ? 'Ver información capturada'
                : 'El prospecto aún no ha contestado la solicitud'
            }
          >
            <Eye className="w-4 h-4" />
          </button>
              </>
            );
          })()}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-3">
          <ClipboardList className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Solicitudes</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            icon={Download}
            onClick={handleExportCsv}
            disabled={exportingCsv}
          >
            {exportingCsv ? 'Exportando…' : 'Exportar CSV'}
          </Button>
          <Button icon={Plus} onClick={handleOpenModal}>
            Crear Solicitud
          </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por prospecto o propiedad..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Todos los estados</option>
            <option value="pending">Pendiente</option>
            <option value="approved">Aprobada</option>
            <option value="rejected">Rechazada</option>
          </select>
        </div>

        {loading && (
          <div className="flex justify-center py-8 text-gray-500">Cargando solicitudes...</div>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">
            {error}
          </div>
        )}
        {!loading && !error && (
          <>
            <Table data={filteredApplications} columns={columns} />
            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </div>

      {/* Modal para Crear Solicitud */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Crear Nueva Solicitud"
        size="lg"
      >
        <ApplicationForm
          properties={properties}
          units={units}
          prospects={prospects}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          isSubmitting={createApplicationMutation.isPending}
        />
      </Modal>

      {/* Modal para Editar Solicitud */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Editar Solicitud"
        size="lg"
      >
        <ApplicationForm
          application={editingApplication}
          properties={properties}
          units={units}
          prospects={prospects}
          onSubmit={handleEditSubmit}
          onCancel={handleCloseEditModal}
          isSubmitting={updateApplicationMutation.isPending}
        />
      </Modal>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

